import { DoseUnit } from './dicomDose';
import {
    formatOnScale,
    IntensityScale,
    STORED_INTENSITY_SCALE,
} from './intensityScale';
import { Modality } from './modality';
import { Image3D } from '../patient/image3d';
import { SuvQuantification } from '../patient/petSuv';
import { WindowLevel } from '../patient/windowLevel';

/**
 * Image intensity units: the label a modality's values carry, and the
 * conversion into that label's scale, so a displayed value is never on a scale
 * its label does not name.
 *
 * Two derivations live here, and they are not interchangeable.
 * {@link formatPetDisplayValue} picks a unit and a precision per value, for a
 * single value read on its own (a hot no-SUV PET voxel compacts to MBq/ml).
 * {@link imageIntensityUnit} + {@link toDisplayIntensity} fix one unit for a
 * whole curve, so every value of a line profile, its statistics and its CSV
 * share one scale. Merging them is the deferred intensity-display retrofit,
 * not a local cleanup.
 */

/** Unit suffixes for image intensity values, per modality. */
export enum ImageUnitSuffix {
    HU = 'HU',
    SUVbw = 'SUVbw [g/ml]',
    kBqPerMl = 'kBq/ml',
    MBqPerMl = 'MBq/ml',
    Gy = 'Gy',
}

/**
 * A formatted value with its unit after it, one space apart, or the value
 * alone when the unit is ''. One spelling wherever a value names its unit.
 */
export function withUnit(text: string, unit: string): string {
    return unit ? `${text} ${unit}` : text;
}

/**
 * The unit label a dose value is read with: `"Gy"` for an absolute dose
 * and `"rel."` for a relative one, a ratio of its own plan that is never
 * comparable across plans. Only a dose stored in Gy is labelled Gy.
 */
export function doseUnitLabel(unit: DoseUnit): string {
    return unit === DoseUnit.GY ? ImageUnitSuffix.Gy : 'rel.';
}

/**
 * How a stored image value is read: Hounsfield units, a PET value in its
 * own units, or a signal intensity with no unit.
 */
export type IntensityKind = 'hounsfield' | 'pet' | 'signal-intensity';

/**
 * How the values of an image of `modality` are read. HU is valid only for
 * CT, a PET value is read in its own units, and every other modality reads
 * as signal intensity. This is the one place that rule is made: a non-CT
 * value labelled HU is a value read wrong.
 */
export function intensityKindOf(modality: string | undefined): IntensityKind {
    switch (modality) {
        case Modality.CT:
            return 'hounsfield';
        case Modality.PT:
            return 'pet';
        default:
            return 'signal-intensity';
    }
}

/**
 * Intensity unit label for an image, e.g. `"HU"`, `"SUVbw [g/ml]"`, or `""`
 * (MR / unitless, and a PET series whose units are unknown). Bare — the
 * caller decides the spacing.
 */
export function imageIntensityUnit(image: Image3D | undefined): string {
    switch (intensityKindOf(image?.firstSlice.modality)) {
        case 'hounsfield':
            return ImageUnitSuffix.HU;
        case 'pet':
            return getPetUnitLabel(
                image?.suvQuantification ?? null,
                image?.maxValue ?? 0
            );
        default:
            return '';
    }
}

/**
 * Returns the compact Bq/ml unit label appropriate for a given raw value magnitude.
 */
function getCompactBqUnit(rawValue: number): ImageUnitSuffix {
    const abs = Math.abs(rawValue);
    if (abs >= 1e6) {
        return ImageUnitSuffix.MBqPerMl;
    }
    return ImageUnitSuffix.kBqPerMl;
}

/**
 * Returns the PET unit label for the volume's quantification state.
 * Only a genuinely Bq-family series (Units=BQML without a usable factor)
 * falls back to the compact Bq notation — a CNTS series is labeled
 * `counts`, a non-BW pre-converted series `SUV (TYPE) [g/ml]`, and a
 * series whose units are unknown gets NO label rather than a wrong one.
 *
 * @param suv - The volume's quantification record, or null when unknown.
 * @param referenceValue - A representative raw pixel value used to pick the
 *   correct Bq prefix (e.g. max of WW and WC). Only used for the Bq fallback.
 */
function getPetUnitLabel(
    suv: SuvQuantification | null,
    referenceValue: number = 0
): string {
    if (suv?.factor != null) {
        return ImageUnitSuffix.SUVbw;
    }
    switch (suv?.state) {
        case 'preconverted-other':
            // SUVType may be absent (implies BW per the PET IOD) when the
            // series was demoted to this state for a missing image
            // correction rather than a non-BW normalization.
            return `SUV (${suv.suvType ?? 'BW'}) [g/ml]`;
        case 'counts':
            return 'counts';
        default:
            return isBqFamily(suv) ? getCompactBqUnit(referenceValue) : '';
    }
}

/**
 * Whether an unquantified series still carries Bq/ml values, and so is
 * scaled and labeled in the compact Bq notation. A series whose units are
 * declared as something else is passed through unscaled; an unknown series
 * (no record at all) is assumed Bq-family, the historical reading.
 */
function isBqFamily(suv: SuvQuantification | null): boolean {
    return suv === null || suv.units === 'BQML';
}

/** Bq/ml scale behind a compact unit label. */
function bqPerMlDivisor(unitLabel: string): number {
    return unitLabel === ImageUnitSuffix.MBqPerMl ? 1e6 : 1e3;
}

/**
 * A raw PET pixel value in the unit `unitLabel` names: SUVbw through the
 * series factor, Bq/ml at that label's prefix for a Bq-family series, and
 * the raw value unchanged otherwise. Pre-converted g/ml and counts values
 * are NEVER rescaled — the ÷1000 that suits Bq/ml would corrupt them.
 *
 * @param rawValue - Raw pixel intensity value.
 * @param suv - The volume's quantification record, or null when unknown.
 * @param unitLabel - The label the value will be shown with.
 */
function toPetDisplayValue(
    rawValue: number,
    suv: SuvQuantification | null,
    unitLabel: string
): number {
    if (suv?.factor != null) {
        return rawValue * suv.factor;
    }
    switch (suv?.state) {
        case 'preconverted-other':
        case 'counts':
            return rawValue;
        default:
            return isBqFamily(suv)
                ? rawValue / bqPerMlDivisor(unitLabel)
                : rawValue;
    }
}

/**
 * Decimal places a PET value is shown with: counts are whole quanta, every
 * other scale reads to a tenth.
 */
function petValueDecimals(suv: SuvQuantification | null): number {
    return suv?.state === 'counts' ? 0 : 1;
}

/**
 * Formats a raw PET pixel value as a numeric string (no unit appended),
 * compacting a hot no-SUV voxel to MBq/ml the way {@link getPetUnitLabel}
 * labels it.
 *
 * @param rawValue - Raw pixel intensity value.
 * @param suv - The volume's quantification record, or null when unknown.
 */
export function formatPetNumericValue(
    rawValue: number,
    suv: SuvQuantification | null
): string {
    const unitLabel = getPetUnitLabel(suv, rawValue);
    return toPetDisplayValue(rawValue, suv, unitLabel).toFixed(
        petValueDecimals(suv)
    );
}

/**
 * The stored intensity a value in the unit `unitLabel` names stands for:
 * the inverse of {@link toPetDisplayValue}, branch for branch. A Bq/ml
 * value is multiplied back by the prefix, not divided by its reciprocal,
 * so a typed value on a bound stores exactly that bound.
 */
function fromPetDisplayValue(
    displayValue: number,
    suv: SuvQuantification | null,
    unitLabel: string
): number {
    if (suv?.factor != null) {
        return displayValue / suv.factor;
    }
    switch (suv?.state) {
        case 'preconverted-other':
        case 'counts':
            return displayValue;
        default:
            return isBqFamily(suv)
                ? displayValue * bqPerMlDivisor(unitLabel)
                : displayValue;
    }
}

/**
 * The scale a PET value is read and typed on under one unit label. Every
 * value on it is scaled by THAT unit: a W/L readout shows two numbers under
 * one label, and a center scaled by its own magnitude next to a width in
 * MBq/ml would read a thousand times off.
 */
function petIntensityScale(
    suv: SuvQuantification | null,
    unitLabel: string
): IntensityScale {
    return {
        unit: unitLabel,
        decimals: petValueDecimals(suv),
        toDisplay: (stored) => toPetDisplayValue(stored, suv, unitLabel),
        toStored: (display) => fromPetDisplayValue(display, suv, unitLabel),
    };
}

/**
 * Formats a raw PET pixel value as a display string with unit appended.
 *
 * @param rawValue - Raw pixel intensity value.
 * @param suv - The volume's quantification record, or null when unknown.
 */
export function formatPetDisplayValue(
    rawValue: number,
    suv: SuvQuantification | null
): string {
    const label = getPetUnitLabel(suv, rawValue);
    const numeric = formatPetNumericValue(rawValue, suv);
    return withUnit(numeric, label);
}

/**
 * A raw sampled value converted to the unit {@link imageIntensityUnit}
 * reports, through the same branches as the label. A whole curve keeps one
 * scale, chosen from the image's hottest voxel, so a cool voxel of a no-SUV
 * PET stays in MBq/ml on the axis where the cursor readout shows it in kBq/ml.
 */
export function toDisplayIntensity(
    value: number,
    image: Image3D | undefined
): number {
    if (image?.firstSlice.modality !== Modality.PT) {
        return value;
    }
    return toPetDisplayValue(
        value,
        image.suvQuantification ?? null,
        imageIntensityUnit(image)
    );
}

/**
 * Decimal places for any intensity already in a display unit, so the chart and
 * the statistics agree. An export re-analyses, so it rounds finer — see
 * `CSV_VALUE_DECIMALS`.
 */
export const INTENSITY_DISPLAY_DECIMALS = 1;

/**
 * Format a display-unit value with its label, e.g. `"45.0 HU"`, `"128.0"`
 * (unitless). The single formatter for line-profile axes, tooltips and stats.
 */
export function formatDisplayIntensity(
    displayValue: number,
    unit: string
): string {
    return withUnit(displayValue.toFixed(INTENSITY_DISPLAY_DECIMALS), unit);
}

/**
 * A raw value of `image` in its display unit, formatted with `unit` (default:
 * the image's own), so two readouts of one image cannot disagree.
 */
export function formatImageIntensity(
    value: number,
    image: Image3D | undefined,
    unit: string = imageIntensityUnit(image)
): string {
    return formatDisplayIntensity(toDisplayIntensity(value, image), unit);
}

/**
 * The scale a series' window/level is read and typed on.
 *
 * A CT or MR window is the stored intensity, a bare whole number. A PET
 * window is on the series' activity or SUV scale: one unit is picked from
 * the larger of the two values, so both sit under one label, and a value
 * typed in that unit lands on the stored intensity it names.
 */
export function windowLevelScaleOf(
    image: Image3D,
    windowLevel: WindowLevel
): IntensityScale {
    if (image.firstSlice.modality !== Modality.PT) {
        return STORED_INTENSITY_SCALE;
    }
    const suv = image.suvQuantification ?? null;
    const unit = getPetUnitLabel(
        suv,
        Math.max(Math.abs(windowLevel.window), Math.abs(windowLevel.level))
    );
    return petIntensityScale(suv, unit);
}

/** A window/level as a readout shows it: two strings on one scale, and that scale. */
export interface WindowLevelReadout {
    /**
     * The unit the readout names beside its two values, or '' when they are
     * shown as bare numbers, as a CT's are.
     */
    readonly unit: string;
    /**
     * The unit the series' values are in, whether or not the readout names
     * it beside its two values: HU for a CT, the readout's unit for a PET,
     * '' for values with no unit.
     */
    readonly valueUnit: string;
    readonly windowWidth: string;
    readonly windowCenter: string;
    /** The scale both values are on, which a typed value is converted through. */
    readonly scale: IntensityScale;
}

/**
 * A series' window/level formatted for reading, on the one scale
 * {@link windowLevelScaleOf} picks for it.
 */
export function formatWindowLevelReadout(
    image: Image3D,
    windowLevel: WindowLevel
): WindowLevelReadout {
    const scale = windowLevelScaleOf(image, windowLevel);
    return {
        unit: scale.unit,
        valueUnit: scale.unit || imageIntensityUnit(image),
        windowWidth: formatOnScale(scale, windowLevel.window),
        windowCenter: formatOnScale(scale, windowLevel.level),
        scale,
    };
}
